import { INVITE_CHANNELS, type InviteChannel, type OutreachAccountStatus } from '@/api/types';

/**
 * Helpers for the admin outreach accounts vault (the logins outreachers DM
 * from). Pure, so the paste parser and the copy are unit-tested.
 */

/** How long a revealed password stays on screen, and how long a copy lives on the clipboard. */
export const REVEAL_SECONDS = 30;

export const ACCOUNT_STATUS_LABEL: Record<OutreachAccountStatus, string> = {
  ACTIVE: 'Active',
  BANNED: 'Banned',
  RETIRED: 'Retired',
};

const PLATFORM_ALIASES: Record<string, InviteChannel> = {
  tiktok: 'TIKTOK',
  tt: 'TIKTOK',
  instagram: 'INSTAGRAM',
  insta: 'INSTAGRAM',
  ig: 'INSTAGRAM',
  youtube: 'YOUTUBE',
  yt: 'YOUTUBE',
  twitter: 'TWITTER',
  x: 'TWITTER',
  discord: 'DISCORD',
  dc: 'DISCORD',
};

export function parsePlatform(raw: string): InviteChannel | null {
  const key = raw.trim().toLowerCase();
  if (PLATFORM_ALIASES[key]) return PLATFORM_ALIASES[key];
  const upper = key.toUpperCase();
  return (INVITE_CHANNELS as readonly string[]).includes(upper) ? (upper as InviteChannel) : null;
}

export interface PastedAccountRow {
  /** 1-based line number in the pasted text, for error messages. */
  line: number;
  platform: InviteChannel | null;
  username: string;
  password: string;
  email: string;
  notes: string;
  /** Why this row can't be saved; null when it's fine. */
  error: string | null;
}

/**
 * Parse "Paste many" text: one account per line,
 *   platform,username,password[,email][,notes]
 * Tab-separated works too (pasting from a sheet), and is the way to go when a
 * password contains a comma. Notes keep any commas after the fifth column.
 * Blank lines and a header row starting with "platform" are skipped. Repeats
 * within the paste are flagged here; the server flags handles already stored.
 */
export function parseAccountPaste(text: string): PastedAccountRow[] {
  const rows: PastedAccountRow[] = [];
  const seen = new Set<string>();
  text.split(/\r?\n/).forEach((rawLine, i) => {
    if (!rawLine.trim()) return;
    const sep = rawLine.includes('\t') ? '\t' : ',';
    const cells = rawLine.split(sep);
    if (rows.length === 0 && cells[0]?.trim().toLowerCase() === 'platform') return;
    const [platformRaw = '', usernameRaw = '', password = '', emailRaw = '', ...rest] = cells;
    const platform = parsePlatform(platformRaw);
    const username = usernameRaw.trim().replace(/^@+/, '');
    const row: PastedAccountRow = {
      line: i + 1,
      platform,
      username,
      // Passwords are kept exactly as typed, but a sheet's trailing CR/space
      // around the separator is almost never part of it.
      password: password.replace(/^\s+|\s+$/g, ''),
      email: emailRaw.trim(),
      notes: rest.join(sep).trim(),
      error: null,
    };
    if (!platform) row.error = `Unknown platform "${platformRaw.trim() || '(blank)'}"`;
    else if (!username) row.error = 'Missing username';
    else {
      const key = `${platform}:${username.toLowerCase()}`;
      if (seen.has(key)) row.error = 'Repeated in this paste';
      seen.add(key);
    }
    rows.push(row);
  });
  return rows;
}

/** One-line description of an audit event, for the history list. */
export function auditActionLabel(action: string): string {
  switch (action) {
    case 'REVEAL':
      return 'Revealed the password';
    case 'COPY':
      return 'Copied the password';
    case 'PASSWORD_SET':
      return 'Set a new password';
    case 'PASSWORD_CLEARED':
      return 'Removed the password';
    case 'DELETE':
      return 'Deleted the account';
    default:
      return action;
  }
}
